import { useState, useRef, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import gsap from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import { globalLenis } from '../index'
gsap.registerPlugin(ScrollToPlugin)
export let globalAudio = null
export let globalAudioStarted = false
let audioTween = null
export function startGlobalAudio() {
  if (globalAudio && !globalAudioStarted) {
    globalAudioStarted = true
    if (audioTween) audioTween.kill()
    globalAudio.volume = 0
    return globalAudio.play()
      .then(() => {
        audioTween = gsap.to(globalAudio, {
          volume: 0.3,
          duration: 2,
          ease: "power2.out"
        })
      })
      .catch(() => {
        globalAudioStarted = false
      })
  }
  return Promise.resolve()
}
export function stopGlobalAudio() {
  if (globalAudio && globalAudioStarted) {
    if (audioTween) audioTween.kill()
    audioTween = gsap.to(globalAudio, {
      volume: 0,
      duration: 1.5,
      ease: "power2.in",
      onComplete: () => {
        globalAudio.pause()
        globalAudioStarted = false
      }
    })
  }
}
const menuSlide = {
  initial: { x: 'calc(100% + 100px)' },
  enter: { x: '0', transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
  exit: { x: 'calc(100% + 100px)', transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
}
const linkSlide = {
  initial: { x: 80 },
  enter: (i) => ({ x: 0, transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.05 * i } }),
  exit: (i) => ({ x: 80, transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1], delay: 0.05 * i } }),
}
const indicatorScale = {
  open: { scale: 1, transition: { duration: 0.3 } },
  closed: { scale: 0, transition: { duration: 0.4 } },
}
const navItems = [
  { title: 'Home', href: '#home' },
  { title: 'About', href: '#about' },
  { title: 'Work', href: '#work' },
  { title: 'Contact', href: '#contact' },
]
function SoundButton() {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  useEffect(() => {
    if (audioRef.current) {
      globalAudio = audioRef.current
    }
  }, [])
  useEffect(() => {
    const checkAudioState = () => {
      if (globalAudioStarted && audioRef.current && !audioRef.current.paused) {
        setIsPlaying(true)
      }
    }
    const audio = audioRef.current
    if (audio) {
      audio.addEventListener('play', () => setIsPlaying(true))
      audio.addEventListener('pause', () => setIsPlaying(false))
    }
    const interval = setInterval(checkAudioState, 100)
    return () => {
      clearInterval(interval)
      if (audio) {
        audio.removeEventListener('play', () => setIsPlaying(true))
        audio.removeEventListener('pause', () => setIsPlaying(false))
      }
    }
  }, [])
  const toggleMusic = () => {
    const audio = audioRef.current
    if (!audio) return
    if (audioTween) audioTween.kill()
    if (!isPlaying) {
      audio.volume = 0
      audio.play().then(() => {
        setIsPlaying(true)
        globalAudioStarted = true
        audioTween = gsap.to(audio, {
          volume: 0.3,
          duration: 1.5,
          ease: "power2.out"
        })
      }).catch(console.error)
    } else {
      audioTween = gsap.to(audio, {
        volume: 0,
        duration: 1,
        ease: "power2.in",
        onComplete: () => {
          audio.pause()
          setIsPlaying(false)
          globalAudioStarted = false
        }
      })
    }
  }
  const bars = [0, 1, 2, 3]
  return (
    <>
      <audio ref={audioRef} src="/music.mp3" preload="auto" loop />
      <motion.button
        onClick={toggleMusic}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="sound-button"
        aria-label={isPlaying ? 'Pause music' : 'Play music'}
        whileTap={{ scale: 0.98 }}
      >
        <motion.span
          className="sound-button__text"
          animate={{ opacity: isHovered || isPlaying ? 1 : 0.5 }}
          transition={{ duration: 0.3 }}
        >
          Sound
        </motion.span>
        <div className="sound-button__bars">
          {bars.map((i) => (
            <motion.span
              key={i}
              className="sound-button__bar"
              animate={isPlaying ? {
                scaleY: [0.3, 1, 0.5, 0.8, 0.3],
                transition: {
                  duration: 1.4,
                  repeat: Infinity,
                  delay: i * 0.15,
                  ease: "easeInOut"
                }
              } : {
                scaleY: 0.3,
                transition: { duration: 0.4 }
              }}
            />
          ))}
        </div>
      </motion.button>
    </>
  )
}
function MenuFooter() {
  return (
    <div className="menu__footer">
      <a href="https://github.com/Sadat-Rakib" target="_blank" rel="noopener noreferrer">GitHub</a>
      <a href="https://www.linkedin.com/in/mir-sadat-bin-rakib/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
      <a href="mailto:mirsadatbinrakib01@gmail.com">Email</a>
      <a href="https://mirrin.vercel.app/" target="_blank" rel="noopener noreferrer">Mirrin</a>
    </div>
  )
}
function MenuCurve() {
  const h = typeof window !== 'undefined' ? window.innerHeight : 0
  const initialPath = `M100 0 L200 0 L200 ${h} L100 ${h} Q-100 ${h / 2} 100 0`
  const targetPath = `M100 0 L200 0 L200 ${h} L100 ${h} Q100 ${h / 2} 100 0`
  const curve = {
    initial: { d: initialPath },
    enter: { d: targetPath, transition: { duration: 1, ease: [0.76, 0, 0.24, 1] } },
    exit: { d: initialPath, transition: { duration: 0.8, ease: [0.76, 0, 0.24, 1] } },
  }
  return (
    <svg className="menu__curve">
      <motion.path variants={curve} initial="initial" animate="enter" exit="exit" />
    </svg>
  )
}
function MenuItem({ data, isActive, setSelected, closeMenu }) {
  const { title, href, index } = data
  const handleClick = (e) => {
    e.preventDefault()
    const el = document.querySelector(href)
    if (el) {
      gsap.to(window, {
        duration: 1.8,
        scrollTo: { y: el, offsetY: 0 },
        ease: "power2.inOut"
      })
    }
    closeMenu()
  }
  return (
    <motion.div
      className="menu__item"
      onMouseEnter={() => setSelected(href)}
      custom={index}
      variants={linkSlide}
      initial="initial"
      animate="enter"
      exit="exit"
    >
      <motion.div
        className="menu__item-dot"
        variants={indicatorScale}
        animate={isActive ? 'open' : 'closed'}
      />
      <a href={href} onClick={handleClick}>{title}</a>
    </motion.div>
  )
}
function Menu({ closeMenu }) {
  const [selected, setSelected] = useState('#home')
  return (
    <motion.div
      className="menu"
      variants={menuSlide}
      initial="initial"
      animate="enter"
      exit="exit"
    >
      {}
      <motion.span
        className="menu__bg-text"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 50 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.76, 0, 0.24, 1] }}
      >
        SADAT
      </motion.span>
      <div className="menu__body">
        <div className="menu__links">
          <p className="menu__title">Navigation</p>
          {navItems.map((item, i) => (
            <MenuItem
              key={item.href}
              data={{ ...item, index: i }}
              isActive={selected === item.href}
              setSelected={setSelected}
              closeMenu={closeMenu}
            />
          ))}
        </div>
        <MenuFooter />
      </div>
      <MenuCurve />
    </motion.div>
  )
}
export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  
  useEffect(() => {
    let ticking = false
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 100)
          ticking = false
        })
        ticking = true
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])
  
  useEffect(() => {
    if (menuOpen) {
      if (globalLenis) globalLenis.stop()
    } else {
      if (globalLenis) globalLenis.start()
    }
    return () => {
      if (globalLenis) globalLenis.start()
    }
  }, [menuOpen])
  useEffect(() => {
    const canvas = document.querySelector('canvas')
    if (!canvas) return
    if (menuOpen) {
      canvas.style.pointerEvents = 'none'
    } else {
      const footer = document.querySelector('footer')
      if (footer) {
        const rect = footer.getBoundingClientRect()
        const isFooterVisible = rect.top < window.innerHeight && rect.bottom > 0
        if (!isFooterVisible) {
          canvas.style.pointerEvents = 'auto'
        }
      } else {
        canvas.style.pointerEvents = 'auto'
      }
    }
  }, [menuOpen])
  return (
    <>
      <header className={`header ${isScrolled ? 'header--scrolled' : ''}`}>
        {}
        <div className="logo">
          <span className="logo__copy">©</span>
          <div className="logo__text">
            <div className="logo__stack">
              <div className="logo__row">
                <span className="logo__code">Code by</span>
                <span className="logo__name">Sadat</span>
              </div>
              <div className="logo__row">
                <span className="logo__name">Sadat</span>
                <span className="logo__surname">Dev</span>
              </div>
            </div>
          </div>
        </div>
        {}
        <div className="header__actions">
          <SoundButton />
          <button
            className={`burger ${menuOpen ? 'burger--active' : ''}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <span className="burger__line" />
            <span className="burger__line" />
          </button>
        </div>
      </header>
      <AnimatePresence mode="wait">
        {menuOpen && <Menu closeMenu={() => setMenuOpen(false)} />}
      </AnimatePresence>
    </>
  )
}